
import React, { useRef, useState, useEffect } from "react";

export default function Invoice({ clientId }) {
    const canvasRef = useRef(null);
    const [showCanvas, setShowCanvas] = useState(false);

    const [invoiceTo, setInvoiceTo] = useState("");
    const [invoiceNo, setInvoiceNo] = useState("");
    const [date, setDate] = useState("");
    const [items, setItems] = useState([
        { sNo: 1, description: "", days: "", rate: "", total: "" },
    ]);

    const [paymentInfo, setPaymentInfo] = useState({
        accountNumber: "",
        beneficiaryName: "",
        beneficiaryAddress: "",
        routingNumber: "",
        bankName: "",
        bankAddress: "",
    });

    const [note, setNote] = useState("");
    const [taxPercent, setTaxPercent] = useState(0);
    const [discountPercent, setDiscountPercent] = useState(0);
    const [dueDate, setDueDate] = useState("");
    const [autoInvoiceUrl, setAutoInvoiceUrl] = useState(null);

    const delay = (ms) => new Promise((res) => setTimeout(res, ms));


    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        // normalize numeric fields
        if (field === "days" || field === "rate" || field === "total") {
            value = Number(value || 0);
        }
        newItems[index][field] = value;
        if (field === "days" || field === "rate") {
            newItems[index].total = newItems[index].days * newItems[index].rate;
        }
        setItems(newItems);
    };

    const addItem = () => {
        setItems([
            ...items,
            { sNo: items.length + 1, description: "", days: "", rate: "", total: "" },
        ]);
    };

    const removeItem = (index) => {
        const newItems = items
            .filter((_, i) => i !== index)
            .map((item, i) => ({ ...item, sNo: i + 1 }));
        setItems(newItems);
    };

    // --- TEXT WRAPPING utility for canvas ---
    function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
        ctx.textAlign = "left";
        const paragraphs = String(text || "").split("\n");
        let currentY = y;
        paragraphs.forEach((para, pIndex) => {
            let words = para.split(" ").filter(Boolean);
            let line = "";
            for (let n = 0; n < words.length; n++) {
                const testLine = line ? line + " " + words[n] : words[n];
                const metrics = ctx.measureText(testLine);
                const testWidth = metrics.width;
                if (testWidth > maxWidth && line) {
                    ctx.fillText(line, x, currentY);
                    line = words[n];
                    currentY += lineHeight;
                } else {
                    line = testLine;
                }
            }
            if (line) {
                ctx.fillText(line, x, currentY);
                currentY += lineHeight;
            }
            if (pIndex < paragraphs.length - 1) currentY += lineHeight / 2;
        });
        return currentY;
    }

    const renderCanvas = ({ returnDataUrl = false } = {}) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const ctx = canvas.getContext("2d");

        // A4 at 300dpi
        const A4_WIDTH = 2480;
        const A4_HEIGHT = 3508;
        canvas.width = A4_WIDTH;
        canvas.height = A4_HEIGHT;

        // White background
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, A4_WIDTH, A4_HEIGHT);

        ctx.fillStyle = "#000000";
        ctx.textBaseline = "top";

        // INVOICE at top-left
        ctx.font = "bold 80px Arial";
        ctx.textAlign = "left";
        ctx.fillText("Invoice", 130, 130);

        // BLUEPLANIT CORPORATION at top-right
        ctx.font = "bold 70px Arial";
        ctx.textAlign = "right";
        ctx.fillStyle = "#8b8989ff";
        ctx.fillText("Blueplanit Corporation", A4_WIDTH - 150, 130);

        // INVOICE FIELDS
        let y = 300;
        const labelFont = "bold 40px Arial";
        const valueFont = "40px Arial";
        ctx.fillStyle = "#000000";
        const leftX = 130;
        const gapY = 60;
        ctx.textAlign = "left";
        // Invoice Number
        ctx.font = labelFont;
        ctx.fillText("Invoice Number:", leftX, y);
        ctx.font = valueFont;
        ctx.fillText(invoiceNo || "", leftX + 500, y);
        y += gapY;
        // Date of Issue
        ctx.font = labelFont;
        ctx.fillText("Date of Issue:", leftX, y);
        ctx.font = valueFont;
        ctx.fillText(date || "", leftX + 500, y);
        y += gapY;
        // Due Date
        ctx.font = labelFont;
        ctx.fillText("Due Date:", leftX, y);
        ctx.font = valueFont;
        ctx.fillText(dueDate || "", leftX + 500, y);
        y += gapY + 70;

        // BLUEPLANIT ADDRESS BLOCK
        ctx.font = "bold 40px Arial";
        ctx.fillText("Blueplanit Corporation", leftX, y);
        y += 70;
        ctx.font = "40px Arial";
        const addressLines = [
            "2108 N. St.,",
            "Suite 4658",
            "Sacramento, California 95816",
            "United States",
            "+1 510-768-7742"
        ];
        addressLines.forEach(line => {
            ctx.fillText(line, leftX, y);
            y += 55;
        });

        // BILL TO SECTION
        ctx.textAlign = "center";
        ctx.font = "bold 40px Arial";
        ctx.fillText("Bill To:", A4_WIDTH - 1350, 550);
        ctx.font = "40px Arial";
        wrapText(ctx, invoiceTo, A4_WIDTH - 1410, y - 290, 600, 30);

        const updatedItems = items.map(item => ({
            ...item,
            total: Number(item.days || 0) * Number(item.rate || 0)

        }));
        const subtotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
        const tax = subtotal * (taxPercent / 100);
        const discountAmount = subtotal * (discountPercent / 100);
        const total = subtotal + tax - discountAmount;
        // const total = subtotal;
        const amountDue = total;

        // Amount Due - complete dynamic(due on)
        ctx.font = "bold 60px Arial";
        ctx.textAlign = "left";
        ctx.fillText(
            total > 0 ? `$${total.toLocaleString("en-IN")} due on ${date || "-"}` : "",
            A4_WIDTH - 2350,
            y + 80
        );
        // For "due on" - static -- 1. Prepare text parts
        // const amountText = total > 0 ? `$${total.toLocaleString("en-IN")}` : "";
        // const dueText = " due on ";
        // const dateText = date || "-";
        // // 2. Position (same line)
        // const x = A4_WIDTH - 2350;
        // const textY = 973;
        // // 3. Draw amount
        // ctx.fillStyle = "#ffffff";
        // ctx.fillText(amountText, x, textY);
        // // 4. Measure amount width
        // const amountWidth = ctx.measureText(amountText).width;
        // // 5. Draw static "due on"
        // ctx.fillStyle = "#000000";
        // ctx.fillText(dueText, x + amountWidth, textY);
        // // 6. Measure "due on" width
        // const dueWidth = ctx.measureText(dueText).width;
        // // 7. Draw date
        // ctx.fillStyle = "#ffffff";
        // ctx.fillText(dateText, x + amountWidth + dueWidth, textY);
        // ctx.fillStyle = "#000000";


        // TABLE HEADERS
        const headerY = 1220;
        ctx.font = "bold 40px Arial";
        ctx.textAlign = "left";
        // Column positions
        const descX = 130;
        const qtyX = 1450;
        const priceX = 1700;
        const amountX = 2170;
        // Draw headers
        ctx.fillText("Description", descX, headerY);
        ctx.fillText("Qty", qtyX, headerY);
        ctx.fillText("Unit Price", priceX, headerY);
        ctx.fillText("Amount", amountX, headerY);
        // Optional separator line under header
        ctx.beginPath();
        ctx.moveTo(130, headerY + 60);
        ctx.lineTo(A4_WIDTH - 130, headerY + 60);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#000000";
        ctx.stroke();

        const rowY = headerY + 90;
        ctx.font = "40px Arial";
        // Values
        let rowOffset = 0;
        updatedItems.forEach((item, i) => {
            const yPos = rowY + rowOffset;
            wrapText(ctx, item.description, descX, yPos, 1200, 50);
            ctx.fillText(item.days ? item.days.toString() : "", qtyX, yPos);
            ctx.fillText(item.rate ? `$${item.rate.toLocaleString("en-IN")}` : "", priceX, yPos);
            ctx.fillText(item.total ? `$${item.total.toLocaleString("en-IN")}` : "", amountX, yPos);

            rowOffset += 70;
        });


        // Position right under the row
        const totalsStartY = rowY + rowOffset + 10 - 70;
        ctx.beginPath();
        ctx.moveTo(1200, totalsStartY + 570);
        ctx.lineTo(A4_WIDTH - 130, totalsStartY + 570);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#b5b5b5ff";
        ctx.stroke();
        ctx.font = "40px Arial";
        ctx.textAlign = "right";
        ctx.fillText("Subtotal", amountX - 820, totalsStartY + 600);
        ctx.textAlign = "left";
        ctx.fillText(subtotal > 0 ? `$${subtotal.toLocaleString("en-IN")}` : "", amountX, totalsStartY + 600);

        const taxY = totalsStartY + 685;
        ctx.beginPath();
        ctx.moveTo(1200, taxY - 20);
        ctx.lineTo(A4_WIDTH - 130, taxY - 20);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#b5b5b5ff";
        ctx.stroke();
        ctx.font = "40px Arial";
        ctx.textAlign = "right";
        ctx.fillText(`Tax (${taxPercent || ""})`, amountX - 828, taxY + 10);
        // Custom drawing to have percentage in different color
        // const startX = amountX - 878;
        // const startY = taxY + 10;
        // // Text parts
        // const labelText = "Tax (";
        // const percentText = `${taxPercent || ""}%`;
        // // Measure widths FIRST
        // const labelWidth = ctx.measureText(labelText).width;
        // const percentWidth = ctx.measureText(percentText).width;
        // // Draw label
        // ctx.fillStyle = "#000";
        // ctx.fillText(labelText, startX, startY);
        // // Draw percentage in white
        // ctx.fillStyle = "#fff";
        // ctx.fillText(percentText, startX + labelWidth, startY);
        // // Draw closing bracket
        // ctx.fillStyle = "#000";
        // ctx.fillText(")", startX + labelWidth + percentWidth, startY);

        ctx.textAlign = "left";
        ctx.fillText(tax > 0 ? `$${tax.toLocaleString("en-IN")}` : "", amountX, taxY + 10);

        const discountY = taxY + 85;
        ctx.beginPath();
        ctx.moveTo(1200, discountY - 20);
        ctx.lineTo(A4_WIDTH - 130, discountY - 20);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#b5b5b5ff";
        ctx.stroke();
        ctx.font = "40px Arial";
        ctx.textAlign = "right";
        ctx.fillText(`Discount (${discountPercent || ""})`, amountX - 825 + 50, discountY + 10);
        ctx.textAlign = "left";
        ctx.fillText(discountAmount > 0 ? `$${discountAmount.toLocaleString("en-IN")}` : "", amountX, discountY + 10);

        const totalY = discountY + 115;
        ctx.beginPath();
        ctx.moveTo(1200, totalY - 40);
        ctx.lineTo(A4_WIDTH - 130, totalY - 40);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#b5b5b5ff";
        ctx.stroke();
        ctx.font = "40px Arial";
        ctx.textAlign = "right";
        ctx.fillText("Total", amountX - 880, totalY - 15);
        ctx.textAlign = "left";
        ctx.fillText(total > 0 ? `$${total.toLocaleString("en-IN")}` : "", amountX, totalY - 15);

        const amountDueY = totalY + 115;
        ctx.beginPath();
        ctx.moveTo(1200, amountDueY - 60);
        ctx.lineTo(A4_WIDTH - 130, amountDueY - 60);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#b5b5b5ff";
        ctx.stroke();
        ctx.font = "bold 40px Arial";
        ctx.textAlign = "right";
        ctx.fillText("Amount due", amountX - 739, amountDueY - 30);
        ctx.textAlign = "left";
        ctx.fillText(
            amountDue > 0 ? `$${amountDue.toLocaleString("en-IN")} USD` : "",
            amountX - 95,
            amountDueY - 30
        );



        // PAYMENT INFO SECTION
        const paymentY = headerY + 1700;
        ctx.font = "bold 50px Arial";
        ctx.textAlign = "left";
        ctx.fillText("Payment Information", 130, paymentY - 15);
        ctx.font = "40px Arial";
        const payGap = 60;
        ctx.fillText(`Account Number: ${paymentInfo.accountNumber || ""}`, 130, paymentY + payGap);
        ctx.fillText(`Bank Name: ${paymentInfo.bankName || ""}`, 130, paymentY + payGap * 2);
        wrapText(ctx, `Bank Address: ${paymentInfo.bankAddress || ""}`, 130, paymentY + payGap * 3, 800, 30);


        // Bottom line
        ctx.beginPath();
        ctx.moveTo(130, headerY + 2080);
        ctx.lineTo(A4_WIDTH - 130, headerY + 2080);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#d1d1d1ff";
        ctx.stroke();

        // FINAL FOOTER INFO (Invoice Number + Amount Due)
        const footerY = headerY + 2150;
        ctx.font = "40px Arial";
        ctx.fillStyle = "#000000";
        ctx.textAlign = "left";
        ctx.fillText(`${invoiceNo}`, 130, footerY);
        ctx.textAlign = "right";
        ctx.fillText(
            amountDue > 0
                ? `$${amountDue.toLocaleString("en-IN")} due ${dueDate || "-"}`
                : `due ${dueDate || "-"}`,
            A4_WIDTH - 1390,
            footerY
        );


        if (returnDataUrl) {
            return canvas.toDataURL("image/png");
        }
        return true;
    };

    const handlePreview = async () => {
        setShowCanvas(true);
        setTimeout(async () => {
            await renderCanvas();
        }, 30);
    };

    const handleDownloadPNG = async () => {
        if (!showCanvas) setShowCanvas(true); // make sure canvas exists
        setTimeout(async () => {
            const dataUrl = await renderCanvas({ returnDataUrl: true });
            if (!dataUrl) return;
            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = `${invoiceNo || "invoice"}.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        }, 50);
    };


    const handlePrintAsPDF = async () => {
        await renderCanvas({ returnDataUrl: false });
        await delay(200);
        const dataUrl = canvasRef.current.toDataURL("image/png");
        const w = window.open("");
        if (!w) return alert("Please allow popups.");
        w.document.write(`
        <html><body style="margin:0;">
            <img src="${dataUrl}" style="width:100%;" />
        </body></html>
    `);
        w.document.close();
        setTimeout(() => w.print(), 500);
    };


    const closeModal = () => setShowCanvas(false);
    useEffect(() => {
        if (showCanvas) {
            renderCanvas();
        }
    }, [showCanvas]);


    useEffect(() => {
        async function fetchAutoInvoice() {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/client/auto-invoice/${clientId}`
                );
                const data = await response.json();
                setAutoInvoiceUrl(data.invoiceImage);
            } catch (err) {
                console.error("Auto invoice load failed", err);
            }
        }

        fetchAutoInvoice();
    }, []);



    return (
        <>
            {autoInvoiceUrl && (
                <div className="auto-invoice-preview">
                    <h2>Auto-Generated Invoice</h2>
                    <img
                        src={autoInvoiceUrl}
                        alt="Auto Invoice"
                        style={{
                            width: "400px",
                            border: "1px solid #ddd",
                            padding: "10px",
                            borderRadius: "10px",
                            display: "block",
                            marginBottom: "20px"
                        }}
                    />
                </div>
            )}


            <div className="invoice-wrapper">
                <div className="invoice-form">
                    <h2>Invoice Details</h2>

                    <div className="form-group">
                        <label>Invoice Number:</label>
                        <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Date of Issue:</label>
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Due Date:</label>
                        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                    </div>

                    <div className="form-group">
                        <label>Bill To (Name / Email):</label>
                        <textarea value={invoiceTo} onChange={(e) => setInvoiceTo(e.target.value)} rows={3} />
                    </div>

                    <h3>Items</h3>
                    <table className="items-table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Description</th>
                                <th>Qty</th>
                                <th>Unit Price</th>
                                <th>Amount</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.sNo}</td>
                                    <td><input value={item.description} onChange={(e) => handleItemChange(index, "description", e.target.value)} /></td>
                                    <td><input type="number" value={item.days} min={0} onChange={(e) => handleItemChange(index, "days", e.target.value)} /></td>
                                    <td><input type="number" value={item.rate} min={0} onChange={(e) => handleItemChange(index, "rate", e.target.value)} /></td>
                                    <td>{item.total}</td>
                                    <td><button onClick={() => removeItem(index)}>Remove</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button onClick={addItem}>Add Item</button>

                    <h3 className="payment-info">Tax and Discount</h3>
                    <div className="form-group"> <label>Tax (%)</label> <input type="number" min={0} value={taxPercent} onChange={(e) =>
                        setTaxPercent(e.target.value === "" ? 0 : Number(e.target.value))
                    }
                    /> </div>
                    <div className="form-group"> <label>Discount (%)</label> <input type="number" min={0} value={discountPercent} onChange={(e) =>
                        setDiscountPercent(e.target.value === "" ? 0 : Number(e.target.value))
                    }
                    /> </div>

                    <h3 className="payment-info">Payment Info</h3>
                    <div className="form-group"><label>Account Number:</label><input value={paymentInfo.accountNumber} onChange={(e) => setPaymentInfo({ ...paymentInfo, accountNumber: e.target.value })} /></div>
                    <div className="form-group"><label>Bank Name:</label><input value={paymentInfo.bankName} onChange={(e) => setPaymentInfo({ ...paymentInfo, bankName: e.target.value })} /></div>
                    <div className="form-group"><label>Bank Address:</label><textarea value={paymentInfo.bankAddress} onChange={(e) => setPaymentInfo({ ...paymentInfo, bankAddress: e.target.value })} rows={3} /></div>

                    <div className="button-row">
                        <button onClick={handlePreview}>Preview Invoice</button>
                        <button onClick={handleDownloadPNG}>Download PNG</button>
                    </div>
                </div>

                {showCanvas && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <button class="close-btn" onClick={closeModal}>×</button>
                            <canvas ref={canvasRef} className="a4-canvas" />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}